import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { email, form, FormField, FormRoot, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationService } from '../../authentication.service';
import { WebLogin } from '../../models';

/**
 * Dialog component for user login.
 * Handles email/password authentication and navigation after successful login.
 * Uses Signal Forms for reactive form state management.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormField,
    FormRoot,
    RouterLink,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
  ],
})
export class LoginComponent {
  private readonly authService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(MatDialogRef<LoginComponent>);

  /** The login form model signal - this IS the form data */
  private readonly loginModel = signal<WebLogin>({
    email: '',
    password: '',
  });

  protected readonly loginError = signal<string | null>(null);

  /** Signal Forms field tree with validation schema */
  protected readonly loginForm = form(
    this.loginModel,
    (schemaPath) => {
      required(schemaPath.email, { message: 'Email is required' });
      email(schemaPath.email, { message: 'Please enter a valid email address' });
      required(schemaPath.password, { message: 'Password is required' });
    },
    {
      submission: {
        action: async (field) => {
          this.loginError.set(null);
          const credentials = field().value();

          try {
            const res = await firstValueFrom(this.authService.login(credentials));
            if (res) {
              this.router.navigate([res[1]]);
            }
            this.dialogRef.close(true);
            return undefined;
          } catch {
            this.loginError.set('Invalid email or password');
            return undefined;
          }
        },
      },
    },
  );
}
